import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import {
  usePurchaseCreditPackageMutation,
  useConfirmCreditPurchaseMutation,
  useGetSavedPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useSavePaymentMethodMutation
} from '../../redux/apis/endpoints/creditApi';
import { useCredits } from '../../hooks/useCredits';
import InviteFriendsButton from './InviteFriendsButton';
import SavedPaymentMethodsSelector from '../checkout/components/SavedPaymentMethodsSelector';
import StripeCardForm from '../checkout/components/StripeCardForm';
import { BACKEND_URL } from '../../utils/config';
import {
  isNativeApp as detectNativeApp,
  isIapAvailable,
  startNativePurchase,
  requestNativeRestore,
  onNativeMessage,
  syncPurchases,
  storeProductId,
  storeManageUrl,
} from '../../utils/nativeIap';
import './CreditPurchaseModal.css';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const BRAND_TEAL = '#14b8a6';

// iosPrice carries a markup to offset Apple's commission (15% on the Small
// Business Program, otherwise 30%) so net revenue roughly matches the web price.
// Must stay in sync with backend_strapi/api/credit-package-purchase/config/products.js
const PACKAGES = [
  { id: 'starter', name: 'Starter Pack', credits: 20, price: 5.99, iosPrice: 6.99, perCreditCost: 0.30, description: '20 AI generations' },
  { id: 'pro', name: 'Pro Pack', credits: 100, price: 19.99, iosPrice: 22.99, popular: true, perCreditCost: 0.20, description: '100 AI generations' }
];

const SUBSCRIPTION_PLANS = [
  { id: 'creator_monthly', name: 'Creator', interval: 'Monthly', credits: 120, price: 14.99, iosPrice: 17.99, perCreditCost: 0.12, description: '120 credits monthly' }
];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch (e) { return ''; }
};

const CARD_STYLE = {
  base: { fontSize: '15px', color: '#333', fontFamily: '"Inter", system-ui, sans-serif', '::placeholder': { color: '#aab7c4' } },
  invalid: { color: '#e74c3c', iconColor: '#e74c3c' }
};

/**
 * Price breakdown once a code is applied, so the discount is visible rather than
 * only implied by a smaller number on the pay button.
 */
const PromoTotal = ({ appliedPromo, suffix = '' }) => (
  <div className="credit-promo-total">
    <div className="credit-promo-total-row">
      <span>Subtotal</span>
      <span>${appliedPromo.originalPrice.toFixed(2)}{suffix}</span>
    </div>
    <div className="credit-promo-total-row credit-promo-total-discount">
      <span>{appliedPromo.code}</span>
      <span>−${appliedPromo.discount.toFixed(2)}{suffix}</span>
    </div>
    <div className="credit-promo-total-row credit-promo-total-due">
      <span>{suffix ? 'Due monthly' : 'Total'}</span>
      <span>${appliedPromo.finalPrice.toFixed(2)}{suffix}</span>
    </div>
  </div>
);

/**
 * Inner form with access to Stripe hooks (must be inside <Elements>).
 * Handles one-time credit packs AND subscriptions.
 */
const CreditPaymentForm = ({ currentBalance, daysUntilReset, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [promoCode, setPromoCode] = useState('');
  // The validated code, as the backend resolved it — this is what the price on
  // screen is derived from, and what gets sent back when the user pays.
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [isCheckingPromo, setIsCheckingPromo] = useState(false);
  const [step, setStep] = useState('select'); // select | payment | subscribe | success
  const [selectedPackage, setSelectedPackage] = useState(PACKAGES.find(p => p.popular) || PACKAGES[0]);
  const [error, setError] = useState(null);
  const [useNewCard, setUseNewCard] = useState(true);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [showCardElement, setShowCardElement] = useState(false);
  const [cardKey] = useState(Date.now());
  const [grantedCredits, setGrantedCredits] = useState(0);
  const [newBalance, setNewBalance] = useState(0);
  const [saveCard, setSaveCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingNative, setIsProcessingNative] = useState(false);
  const [nativeError, setNativeError] = useState(null);
  const [isSubSuccess, setIsSubSuccess] = useState(false);

  const isNativeApp = detectNativeApp();
  const iapAvailable = isIapAvailable();
  // Show App Store pricing inside the native wrapper, web pricing in the browser.
  // Apple rejects apps whose displayed price differs from what the store charges.
  const displayPrice = (p) => (isNativeApp && p.iosPrice ? p.iosPrice : p.price);
  const [isRestoring, setIsRestoring] = useState(false);
  const [nativeNotice, setNativeNotice] = useState(null);
  // Which product the native purchase is for, captured when the purchase starts
  // so the success handler is not at the mercy of what is selected by the time
  // the store sheet closes.
  const pendingPurchase = useRef(null);
  const [, setSearchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);

  const { data: savedPaymentMethods = [] } = useGetSavedPaymentMethodsQuery();
  const [purchaseCredits] = usePurchaseCreditPackageMutation();
  const [confirmPurchase] = useConfirmCreditPurchaseMutation();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [savePaymentMethod] = useSavePaymentMethodMutation();

  // Load subscription status
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/credit-packages/subscription-status`, { headers: authHeaders() });
        if (res.ok && active) setSubscription(await res.json());
      } catch (e) { /* non-fatal */ }
    })();
    return () => { active = false; };
  }, []);

  const openManageSubscription = useCallback(() => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('manage', 'subscription'); return next; });
    if (onClose) onClose();
  }, [setSearchParams, onClose]);

  // Default-select saved card
  useEffect(() => {
    if (savedPaymentMethods.length > 0) {
      setSelectedSavedMethod(savedPaymentMethods.find(m => m.isDefault) || savedPaymentMethods[0]);
      setUseNewCard(false);
    } else {
      setUseNewCard(true);
      setSelectedSavedMethod(null);
    }
  }, [savedPaymentMethods]);

  // Native IAP handler.
  //
  // A store purchase only means Apple/Google took the money. Credits exist once
  // OUR backend has verified the purchase with RevenueCat, so success is reported
  // from the backend's answer — never from the store sheet closing.
  useEffect(() => {
    if (!isNativeApp) return undefined;

    return onNativeMessage(async (type, payload) => {
      if (type === 'NATIVE_PURCHASE_SUCCESS') {
        const pending = pendingPurchase.current;
        setNativeNotice('Confirming your purchase…');
        try {
          const result = await syncPurchases();
          setGrantedCredits(result.creditsGranted);
          setNewBalance(result.creditBalance);
          setIsSubSuccess(!!(pending && pending.isSubscription));
          if (pending && pending.isSubscription) {
            setSubscription({ active: true, plan: 'creator', provider: 'app_store' });
          }
          setStep('success');
          if (onSuccess) {
            onSuccess({
              creditsGranted: result.creditsGranted,
              creditBalance: result.creditBalance,
              isSubscription: !!(pending && pending.isSubscription),
            });
          }
        } catch (err) {
          // The money is gone but we could not confirm it. Say so honestly and
          // point at Restore, which retries the same verification.
          setNativeError(
            `${err.message} Your payment went through — tap "Restore purchases" to try again, ` +
            'or contact support if it keeps failing.'
          );
        } finally {
          setNativeNotice(null);
          setIsProcessingNative(false);
          pendingPurchase.current = null;
        }
        return;
      }

      if (type === 'NATIVE_PURCHASE_CANCELLED') {
        // Backing out of the store sheet is not an error.
        setIsProcessingNative(false);
        pendingPurchase.current = null;
        return;
      }

      if (type === 'NATIVE_PURCHASE_ERROR') {
        setNativeError(payload.error || 'The purchase could not be completed.');
        setIsProcessingNative(false);
        pendingPurchase.current = null;
        return;
      }

      if (type === 'NATIVE_RESTORE_SUCCESS') {
        try {
          const result = await syncPurchases({ attempts: 2 });
          setNewBalance(result.creditBalance);
          if (result.creditsGranted > 0) {
            setGrantedCredits(result.creditsGranted);
            setStep('success');
            if (onSuccess) onSuccess({ creditsGranted: result.creditsGranted, creditBalance: result.creditBalance });
          } else {
            setNativeNotice('Everything on this account is already up to date.');
          }
        } catch (err) {
          setNativeError(err.message);
        } finally {
          setIsRestoring(false);
        }
        return;
      }

      if (type === 'NATIVE_RESTORE_ERROR') {
        setNativeError(payload.error || 'Could not restore purchases.');
        setIsRestoring(false);
      }
    });
  }, [isNativeApp, onSuccess]);

  const beginNativePurchase = useCallback((product, isSubscription) => {
    setNativeError(null);
    setNativeNotice(null);
    if (!iapAvailable) {
      setNativeError('In-app purchases are not available on this device.');
      return;
    }
    pendingPurchase.current = { product, isSubscription };
    setIsProcessingNative(true);
    startNativePurchase(storeProductId(product));
  }, [iapAvailable]);

  const handleRestore = useCallback(() => {
    setNativeError(null);
    setNativeNotice(null);
    setIsRestoring(true);
    requestNativeRestore();
  }, []);

  const handleCardReady = useCallback(() => setShowCardElement(true), []);
  const handleCardChange = useCallback((event) => { setError(event.error ? event.error.message : null); }, []);


  // What the promo, if any, is being applied to right now.
  const promoTarget = step === 'subscribe'
    ? { kind: 'subscription', productId: SUBSCRIPTION_PLANS[0].id }
    : { kind: 'pack', productId: selectedPackage?.id };

  // An applied code is tied to one product. Switching packs (or between a pack
  // and the subscription) makes it stale, so it is dropped rather than left on
  // screen quoting a price that no longer applies.
  useEffect(() => {
    setAppliedPromo(prev => (
      prev && prev.kind === promoTarget.kind && prev.productId === promoTarget.productId ? prev : null
    ));
    setPromoError(null);
  }, [promoTarget.kind, promoTarget.productId]);

  const basePrice = step === 'subscribe' ? SUBSCRIPTION_PLANS[0].price : (selectedPackage?.price || 0);
  const payablePrice = appliedPromo ? appliedPromo.finalPrice : basePrice;
  const isFreeOrder = !!appliedPromo && appliedPromo.free;

  const applyPromo = async () => {
    const code = promoCode.trim();
    if (isProcessing || isCheckingPromo || !code) return;
    setIsCheckingPromo(true); setPromoError(null); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/credit-packages/validate-promo`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ code, ...promoTarget })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(typeof data.message === 'string' ? data.message : "That promo code isn't valid.");
      }
      setAppliedPromo(data);
      setPromoCode(data.code);
    } catch (e) { setPromoError(e.message || 'Promo code could not be applied.'); }
    finally { setIsCheckingPromo(false); }
  };

  const removePromo = () => { setAppliedPromo(null); setPromoCode(''); setPromoError(null); };

  // --- One-time pack purchase ---
  const handleSubmit = async () => {
    // A fully-discounted order never reaches Stripe, so it must not wait on it.
    if ((!stripe && !isFreeOrder) || !selectedPackage || isProcessing) return;
    setError(null);
    setIsProcessing(true);
    try {
      const purchaseResult = await purchaseCredits({
        packageId: selectedPackage.id,
        savedPaymentMethodId: (!useNewCard && selectedSavedMethod) ? selectedSavedMethod.id : undefined,
        promoCode: appliedPromo?.code
      }).unwrap();
      if (!purchaseResult.success) throw new Error(purchaseResult.message || 'Failed to create payment');

      // Nothing left to charge — the backend already granted the credits.
      if (purchaseResult.freeOrder) {
        setGrantedCredits(purchaseResult.creditsGranted || selectedPackage.credits);
        setNewBalance(purchaseResult.creditBalance || 0);
        setStep('success');
        if (onSuccess) onSuccess({ creditsGranted: purchaseResult.creditsGranted, creditBalance: purchaseResult.creditBalance });
        return;
      }

      const { clientSecret, paymentIntentId: piId, paymentStatus } = purchaseResult;
      let stripeResult;
      if (paymentStatus === 'succeeded') {
        stripeResult = { paymentIntent: { id: piId, status: 'succeeded' } };
      } else if (!useNewCard && selectedSavedMethod) {
        stripeResult = selectedSavedMethod.stripePaymentMethodId
          ? await stripe.confirmCardPayment(clientSecret, { payment_method: selectedSavedMethod.stripePaymentMethodId })
          : await stripe.confirmCardPayment(clientSecret);
      } else {
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card form not loaded. Please refresh and try again.');
        stripeResult = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: cardElement } });
      }
      if (stripeResult.error) throw new Error(stripeResult.error.message);

      // Save card if requested
      if (saveCard && useNewCard && stripeResult.paymentIntent?.status === 'succeeded') {
        try {
          const setupResult = await createSetupIntent().unwrap();
          const cardEl = elements.getElement(CardElement);
          if (cardEl) {
            const confirmSetup = await stripe.confirmCardSetup(setupResult.clientSecret, { payment_method: { card: cardEl } });
            if (!confirmSetup.error) await savePaymentMethod({ setupIntentId: confirmSetup.setupIntent.id, isDefault: savedPaymentMethods.length === 0 }).unwrap();
          }
        } catch (saveErr) { console.warn('Failed to save card:', saveErr); }
      }

      const confirmResult = await confirmPurchase({ paymentIntentId: stripeResult.paymentIntent.id }).unwrap();
      if (confirmResult.success) {
        setGrantedCredits(confirmResult.creditsGranted || selectedPackage.credits);
        setNewBalance(confirmResult.creditBalance || 0);
        setStep('success');
        if (onSuccess) onSuccess({ creditsGranted: confirmResult.creditsGranted, creditBalance: confirmResult.creditBalance });
      } else { throw new Error('Credit confirmation failed'); }
    } catch (err) { setError(err.message || 'Payment failed. Please try again.'); }
    finally { setIsProcessing(false); }
  };

  // --- Embedded subscription ---
  const handleSubscribe = async () => {
    setNativeError(null);
    if (isNativeApp) {
      beginNativePurchase(SUBSCRIPTION_PLANS[0], true);
      return;
    }
    if (!stripe && !isFreeOrder) { setError('Payment system not ready.'); return; }
    setIsProcessing(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/credit-packages/subscribe-embedded`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ planId: 'creator_monthly', savedPaymentMethodId: (!useNewCard && selectedSavedMethod) ? selectedSavedMethod.id : undefined, returnUrl: window.location.href, promoCode: appliedPromo?.code })
      });
      const data = await res.json();
      // A fully-discounted subscription has no first invoice to pay, so Stripe
      // returns it already active with no PaymentIntent to confirm.
      const alreadyActive = data.paymentStatus === 'succeeded'
        || ['active', 'trialing'].includes(data.subscriptionStatus);
      if (!data.success || (!data.clientSecret && !alreadyActive)) throw new Error(data.message || 'Failed to create subscription');

      if (!alreadyActive && data.clientSecret) {
        let confirmResult;
        if (!useNewCard && selectedSavedMethod?.stripePaymentMethodId) {
          confirmResult = await stripe.confirmCardPayment(data.clientSecret, { payment_method: selectedSavedMethod.stripePaymentMethodId });
        } else if (!useNewCard && selectedSavedMethod) {
          confirmResult = await stripe.confirmCardPayment(data.clientSecret);
        } else {
          const cardElement = elements.getElement(CardElement);
          if (!cardElement) throw new Error('Card form not loaded. Please refresh and try again.');
          confirmResult = await stripe.confirmCardPayment(data.clientSecret, { payment_method: { card: cardElement } });
        }
        if (confirmResult.error) throw new Error(confirmResult.error.message);
      }

      const confirmRes = await fetch(`${BACKEND_URL}/credit-packages/confirm-subscription`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ subscriptionId: data.subscriptionId })
      });
      const confirmData = await confirmRes.json();
      if (!confirmData.success) throw new Error(confirmData.message || 'Subscription activation failed');

      setGrantedCredits(120);
      setNewBalance(confirmData.creditBalance || 0);
      setSubscription({ active: true, plan: 'creator' });
      setIsSubSuccess(true);
      setStep('success');
      if (onSuccess) onSuccess({ creditsGranted: 120, creditBalance: confirmData.creditBalance, isSubscription: true });
    } catch (err) { setError(err.message || 'Failed to start subscription.'); }
    finally { setIsProcessing(false); }
  };


  // --- Success ---
  if (step === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#059669' }}>
          <svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" /><path d="M14 24l7 7 13-13" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>
          {isSubSuccess ? "You're subscribed to Creator!" : `+${grantedCredits} Credits Added`}
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
          {isSubSuccess
            ? `${grantedCredits} credits added · renews monthly. Balance: ${newBalance}`
            : `New balance: ${newBalance} credits`}
        </div>
        <button onClick={onClose} style={{ marginTop: 20, padding: '10px 32px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Back to try-on
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Balance Row */}
      {currentBalance !== null && currentBalance !== undefined && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f9fafb', borderRadius: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Current balance</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: currentBalance > 0 ? BRAND_TEAL : '#ef4444' }}>{currentBalance} credits</span>
        </div>
      )}



      {/* --- Package selection --- */}
      {step === 'select' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
              {PACKAGES.map(pkg => {
                const selected = selectedPackage?.id === pkg.id;
                return (
                  <div key={pkg.id} onClick={() => setSelectedPackage(pkg)} role="radio" aria-checked={selected} tabIndex={0}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: `2px solid ${selected ? BRAND_TEAL : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: selected ? '#f0fdfa' : '#fff', transition: 'all 0.15s' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selected ? BRAND_TEAL : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {selected && <div style={{ width: 10, height: 10, borderRadius: '50%', background: BRAND_TEAL }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {pkg.name}
                        {pkg.popular && <span style={{ fontSize: 10, fontWeight: 600, color: BRAND_TEAL, background: '#ccfbf1', padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>Popular</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{pkg.credits} AI generations · ${pkg.perCreditCost.toFixed(2)}/credit</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>${displayPrice(pkg).toFixed(2)}</div>
                  </div>
                );
              })}
            </div>

          {nativeError && <div style={{ color: '#ef4444', fontSize: 13, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, marginBottom: 12 }}>{nativeError}</div>}
          {nativeNotice && !nativeError && <div style={{ color: '#0f766e', fontSize: 13, padding: '8px 12px', background: '#f0fdfa', borderRadius: 8, marginBottom: 12 }}>{nativeNotice}</div>}

          {isProcessingNative ? (
            <div style={{ padding: '20px 0', color: BRAND_TEAL, fontWeight: 600, fontSize: 14, textAlign: 'center' }}>
              <style>{`@keyframes pulse { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }`}</style>
              <div style={{ animation: 'pulse 1.5s infinite' }}>
                {nativeNotice || 'Processing App Store Payment...'}
              </div>
            </div>
          ) : selectedPackage && (
            <button onClick={() => {
              if (isNativeApp) beginNativePurchase(selectedPackage, false);
              else setStep('payment');
            }} disabled={!selectedPackage || isProcessingNative}
              style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 8, opacity: selectedPackage && !isProcessingNative ? 1 : 0.5 }}>
              Continue · ${displayPrice(selectedPackage).toFixed(2)}
            </button>
          )}


          {/* Subscription section */}
          <>
              <div className="credit-divider"><span>generate often?</span></div>
              {SUBSCRIPTION_PLANS.map((plan) => {
                const planKey = plan.id.replace('_monthly', '');
                const isActive = subscription?.active && subscription.plan === planKey;
                return (
                  <div key={plan.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>{plan.name}</span>
                        <span style={{ fontSize: 11, fontWeight: 500, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>{plan.interval}</span>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>${displayPrice(plan).toFixed(2)}<span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>/mo</span></span>
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
                      {plan.credits} credits monthly · ${plan.perCreditCost.toFixed(2)}/credit
                    </div>
                    {isActive ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: BRAND_TEAL, background: '#e6f7f6', padding: '2px 10px', borderRadius: 9999 }}>Active</span>
                          {subscription.cancelAtPeriodEnd
                            ? <span style={{ fontSize: 12, color: '#6b7280' }}>Ends {fmtDate(subscription.renewsAt)} · credits kept until then</span>
                            : (subscription.renewsAt && <span style={{ fontSize: 12, color: '#6b7280' }}>Renews {fmtDate(subscription.renewsAt)}</span>)}
                        </div>
                        {subscription.provider && subscription.provider !== 'stripe' ? (
                          // Apple and Google require that store subscriptions be
                          // cancelled in their own UI, so link out rather than
                          // offering an in-app cancel that cannot work.
                          <a href={subscription.manageUrl || storeManageUrl()} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'block', textAlign: 'center', width: '100%', padding: '10px', boxSizing: 'border-box', background: 'transparent', color: '#374151', border: '1px solid #d1d5db', borderRadius: 9999, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                            Manage in the {subscription.provider === 'play_store' ? 'Play Store' : 'App Store'}
                          </a>
                        ) : (
                          <button onClick={openManageSubscription}
                            style={{ width: '100%', padding: '10px', background: 'transparent', color: '#374151', border: '1px solid #d1d5db', borderRadius: 9999, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            Manage subscription
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <button onClick={() => { if (isNativeApp) handleSubscribe(); else setStep('subscribe'); }} disabled={isProcessingNative}
                          style={{ width: '100%', padding: '10px', background: '#fff', color: '#111', border: '1px solid #d1d5db', borderRadius: '9999px', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.target.style.background = '#f9fafb'; e.target.style.borderColor = BRAND_TEAL; }}
                          onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.borderColor = '#d1d5db'; }}>
                          Go {plan.name} monthly
                        </button>
                        <div style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Cancel anytime</div>
                      </>
                    )}
                  </div>
                );
              })}
            </>

          {/* Restore purchases — required by App Store Guideline 3.1.1, and the
              recovery path when a purchase went through but the credits did not
              land (app killed mid-flight, network dropped during verification). */}
          {isNativeApp && (
            <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 4 }}>
              <button onClick={handleRestore} disabled={isRestoring || isProcessingNative}
                style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, textDecoration: 'underline', cursor: isRestoring ? 'default' : 'pointer', padding: 6 }}>
                {isRestoring ? 'Restoring…' : 'Restore purchases'}
              </button>
            </div>
          )}

          <div style={{ marginTop: 8 }}><InviteFriendsButton variant="text" /></div>
        </>
      )}

      {/* --- Promo code --- */}
      {!isNativeApp && ['payment', 'subscribe'].includes(step) && (
        <div className="credit-promo">
          {appliedPromo ? (
            <div className="credit-promo-applied">
              <svg className="credit-promo-tick" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M6 10.5l2.5 2.5L14 7.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="credit-promo-applied-text">
                <span className="credit-promo-applied-code">{appliedPromo.code}</span>
                <span className="credit-promo-applied-label">{appliedPromo.label}</span>
              </div>
              <button type="button" className="credit-promo-remove" onClick={removePromo} disabled={isProcessing}>Remove</button>
            </div>
          ) : (
            <>
              <label className="credit-promo-label" htmlFor="credit-promo">Promo code</label>
              <div className="credit-promo-row">
                <input id="credit-promo" className="credit-promo-input" value={promoCode} autoComplete="off"
                  placeholder="Enter code" spellCheck={false} disabled={isProcessing || isCheckingPromo}
                  onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }} />
                <button type="button" className="credit-promo-apply" onClick={applyPromo}
                  disabled={isProcessing || isCheckingPromo || !promoCode.trim()}>
                  {isCheckingPromo ? 'Checking…' : 'Apply'}
                </button>
              </div>
            </>
          )}
          {promoError && <div className="credit-promo-error">{promoError}</div>}
        </div>
      )}
      {/* --- One-time pack payment --- */}
      {step === 'payment' && (
        <>
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
          {appliedPromo && <PromoTotal appliedPromo={appliedPromo} />}
          {/* A fully-discounted order takes no card at all — asking for one
              would be collecting details we have no reason to hold. */}
          {!isFreeOrder && (
            <div style={{ opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
              <SavedPaymentMethodsSelector savedPaymentMethods={savedPaymentMethods} selectedSavedMethod={selectedSavedMethod}
                useNewCard={useNewCard} onSelectedMethodChange={setSelectedSavedMethod} onUseNewCardChange={setUseNewCard} />
              {useNewCard && (
                <>
                  <StripeCardForm cardKey={cardKey} showCardElement={showCardElement || true}
                    onCardChange={handleCardChange} onCardReady={handleCardReady} saveCard={saveCard} />
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 12 }}>
                    <input type="checkbox" checked={saveCard} onChange={() => setSaveCard(!saveCard)}
                      style={{ width: 16, height: 16, accentColor: BRAND_TEAL, cursor: 'pointer' }} />
                    <span style={{ marginLeft: 8, fontSize: 13, color: '#374151' }}>Save this card for future purchases</span>
                  </label>
                </>
              )}
            </div>
          )}

          {error && <div style={{ color: '#ef4444', fontSize: 13, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, marginBottom: 8 }}>{error}</div>}

          {(() => {
            const blocked = isProcessing || (!isFreeOrder && (!stripe || (useNewCard && !showCardElement)));
            return (
              <button onClick={handleSubmit} disabled={blocked}
                style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8, opacity: blocked ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {isProcessing && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'creditSpin 1s linear infinite', display: 'inline-block' }} />}
                {isProcessing ? 'Processing...'
                  : isFreeOrder ? `Get ${selectedPackage?.credits} Credits free`
                  : `Pay $${payablePrice.toFixed(2)} for ${selectedPackage?.credits} Credits`}
              </button>
            );
          })()}

          {!isProcessing && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => setStep('select')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280' }}>Back to packages</button>
            </div>
          )}
        </>
      )}

      {/* --- Subscription payment --- */}
      {step === 'subscribe' && (
        <>
          <div style={{ padding: '8px 0 12px', fontSize: 14, fontWeight: 600, color: '#111' }}>
            Creator Monthly · $14.99/mo
            <div style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginTop: 2 }}>120 credits monthly · cancel anytime</div>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0 12px' }} />
          {appliedPromo && <PromoTotal appliedPromo={appliedPromo} suffix="/mo" />}
          {!isFreeOrder && (
            <div style={{ opacity: isProcessing ? 0.5 : 1, pointerEvents: isProcessing ? 'none' : 'auto' }}>
              <SavedPaymentMethodsSelector savedPaymentMethods={savedPaymentMethods} selectedSavedMethod={selectedSavedMethod}
                useNewCard={useNewCard} onSelectedMethodChange={setSelectedSavedMethod} onUseNewCardChange={setUseNewCard} />
              {useNewCard && (
                <StripeCardForm cardKey={cardKey} showCardElement={showCardElement || true}
                  onCardChange={handleCardChange} onCardReady={handleCardReady} saveCard={false} />
              )}
            </div>
          )}

          {error && <div style={{ color: '#ef4444', fontSize: 13, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, marginBottom: 8 }}>{error}</div>}

          {(() => {
            const blocked = isProcessing || (!isFreeOrder && (!stripe || (useNewCard && !showCardElement)));
            return (
              <button onClick={handleSubscribe} disabled={blocked}
                style={{ width: '100%', padding: '12px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8, opacity: blocked ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {isProcessing && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'creditSpin 1s linear infinite', display: 'inline-block' }} />}
                {isProcessing ? 'Processing...'
                  : isFreeOrder ? 'Start Creator · free'
                  : `Subscribe · $${payablePrice.toFixed(2)}/mo`}
              </button>
            );
          })()}

          {!isProcessing && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" onClick={() => setStep('select')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280' }}>Back to packages</button>
            </div>
          )}
        </>
      )}
    </>
  );
};

/**
 * CreditPurchaseModal -- portal overlay wrapping Elements provider.
 */
const CreditPurchaseModal = ({ isOpen, onClose }) => {
  const overlayRef = useRef(null);
  const { balance, daysUntilReset, refetch, updateBalance } = useCredits();

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose(); };

  const handleSuccess = (result) => {
    if (result?.creditBalance != null) updateBalance({ balance: result.creditBalance });
    refetch();
  };

  return ReactDOM.createPortal(
    <>
      <style>{`@keyframes susuCreditSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes creditSpin { to { transform: rotate(360deg); } }`}</style>
      <div ref={overlayRef} onClick={handleOverlayClick}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%', maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', animation: 'susuCreditSlideUp 0.25s ease', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>Get More Credits</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', lineHeight: 1, padding: '4px' }}>&#x2715;</button>
          </div>
          <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <CreditPaymentForm currentBalance={balance} daysUntilReset={daysUntilReset} onSuccess={handleSuccess} onClose={onClose} />
              </Elements>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: 14 }}>
                Payment system is not configured. Please try again later.
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CreditPurchaseModal;
